// MIT License - Copyright (c) 2026 Doug McNall

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import CrewManager from '../utils/game/CrewManager';
import CrewRelationshipManager, { RELATIONSHIP_STATUS } from '../utils/game/CrewRelationshipManager';
import './CrewRelationshipModal.css';

const CrewRelationshipModal = ({ isOpen, crewMember, onClose, embedded = false }) => {
  const [relationships, setRelationships] = useState({});
  const [allCrew, setAllCrew] = useState([]);
  const [selectedRelationship, setSelectedRelationship] = useState(null);

  useEffect(() => {
    if (isOpen && crewMember) {
      const crew = CrewManager.getAllCrew();
      setAllCrew(crew);
      setRelationships(CrewRelationshipManager.getAllRelationships(crewMember.id));
    }
  }, [isOpen, crewMember]);

  if (!isOpen || !crewMember) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case RELATIONSHIP_STATUS.CLOSE: return '#00ff88';
      case RELATIONSHIP_STATUS.FRIENDLY: return '#88ff00';
      case RELATIONSHIP_STATUS.NEUTRAL: return '#ffaa00';
      case RELATIONSHIP_STATUS.DISTANT: return '#ff8800';
      case RELATIONSHIP_STATUS.HOSTILE: return '#ff4444';
      default: return '#888';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case RELATIONSHIP_STATUS.CLOSE: return '💚';
      case RELATIONSHIP_STATUS.FRIENDLY: return '😊';
      case RELATIONSHIP_STATUS.NEUTRAL: return '😐';
      case RELATIONSHIP_STATUS.DISTANT: return '😕';
      case RELATIONSHIP_STATUS.HOSTILE: return '😠';
      default: return '❓';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getRelationshipTrend = (history) => {
    if (history.length < 2) return 'stable';
    const recent = history.slice(-5);
    const totalChange = recent.reduce((sum, h) => sum + h.scoreChange, 0);
    if (totalChange > 5) return 'improving';
    if (totalChange < -5) return 'declining';
    return 'stable';
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving': return '📈';
      case 'declining': return '📉';
      default: return '➡️';
    }
  };

  const content = (
    <>
      <div className="crew-relationship-modal" onClick={(e) => e.stopPropagation()}>
        <div className="crew-relationship-header">
          <h2>🤝 {crewMember.name}'s Relationships</h2>
          {!embedded && <button className="close-btn" onClick={onClose}>✕</button>}
        </div>

        <div className="crew-relationship-body">
          <div className="relationship-graph">
            <div className="graph-title">Relationship Network</div>
            <div className="graph-container">
              <div className="center-node">
                <div className="crew-node main-crew">
                  <span className="crew-icon">{crewMember.roleData.icon}</span>
                  <span className="crew-name">{crewMember.name}</span>
                </div>
              </div>
              
              <div className="relationship-nodes">
                {allCrew
                  .filter(c => c.id !== crewMember.id)
                  .map((otherCrew, index) => {
                    const rel = relationships[otherCrew.id] || { score: 50, status: RELATIONSHIP_STATUS.NEUTRAL, history: [] };
                    const angle = (index * 360) / (allCrew.length - 1);
                    const radius = 120;
                    const x = Math.cos(angle * Math.PI / 180) * radius;
                    const y = Math.sin(angle * Math.PI / 180) * radius;
                    
                    return (
                      <div key={otherCrew.id}>
                        <div 
                          className="relationship-line"
                          style={{
                            transform: `rotate(${angle}deg)`,
                            width: `${radius}px`,
                            borderColor: getStatusColor(rel.status),
                            opacity: rel.score / 100
                          }}
                        />
                        <div 
                          className="crew-node other-crew"
                          style={{
                            transform: `translate(${x}px, ${y}px)`,
                            borderColor: getStatusColor(rel.status)
                          }}
                          onClick={() => setSelectedRelationship({ crew: otherCrew, relationship: rel })}
                        >
                          <span className="crew-icon">{otherCrew.roleData.icon}</span>
                          <span className="crew-name">{otherCrew.name}</span>
                          <span className="relationship-score">{Math.floor(rel.score)}</span>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>

          <div className="relationship-list">
            <div className="list-title">All Relationships</div>
            {allCrew
              .filter(c => c.id !== crewMember.id)
              .map(otherCrew => {
                const rel = relationships[otherCrew.id] || { score: 50, status: RELATIONSHIP_STATUS.NEUTRAL, history: [] };
                const trend = getRelationshipTrend(rel.history);
                
                return (
                  <div 
                    key={otherCrew.id}
                    className={`relationship-item ${selectedRelationship?.crew.id === otherCrew.id ? 'selected' : ''}`}
                    onClick={() => setSelectedRelationship({ crew: otherCrew, relationship: rel })}
                  >
                    <div className="relationship-crew">
                      <span className="crew-icon">{otherCrew.roleData.icon}</span>
                      <span className="crew-name">{otherCrew.name}</span>
                    </div>
                    <div className="relationship-status">
                      <span className="status-icon">{getStatusIcon(rel.status)}</span>
                      <span className="status-text" style={{ color: getStatusColor(rel.status) }}>
                        {rel.status}
                      </span>
                      <span className="trend-icon">{getTrendIcon(trend)}</span>
                    </div>
                    <div className="relationship-score">
                      <div className="score-bar">
                        <div 
                          className="score-fill"
                          style={{ 
                            width: `${rel.score}%`,
                            backgroundColor: getStatusColor(rel.status)
                          }}
                        />
                      </div>
                      <span className="score-text">{Math.floor(rel.score)}</span>
                    </div>
                  </div>
                );
              })}
          </div>

          {selectedRelationship && (
            <div className="relationship-details">
              <div className="details-title">
                Relationship with {selectedRelationship.crew.name}
              </div>
              
              <div className="relationship-summary">
                <div className="summary-item">
                  <span className="summary-label">Status:</span>
                  <span 
                    className="summary-value"
                    style={{ color: getStatusColor(selectedRelationship.relationship.status) }}
                  >
                    {getStatusIcon(selectedRelationship.relationship.status)} {selectedRelationship.relationship.status}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Score:</span>
                  <span className="summary-value">{Math.floor(selectedRelationship.relationship.score)}/100</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Trend:</span>
                  <span className="summary-value">
                    {getTrendIcon(getRelationshipTrend(selectedRelationship.relationship.history))} 
                    {getRelationshipTrend(selectedRelationship.relationship.history)}
                  </span>
                </div>
              </div>

              <div className="interaction-history">
                <div className="history-title">Recent Interactions</div>
                <div className="history-timeline">
                  {selectedRelationship.relationship.history.length === 0 ? (
                    <div className="no-history">No interactions yet</div>
                  ) : (
                    selectedRelationship.relationship.history
                      .slice(-10)
                      .reverse()
                      .map((interaction, index) => (
                        <div key={index} className="history-item">
                          <div className="history-time">
                            {formatTimestamp(interaction.timestamp)}
                          </div>
                          <div className="history-interaction">
                            {interaction.interaction}
                          </div>
                          <div 
                            className="history-change"
                            style={{ 
                              color: interaction.scoreChange > 0 ? '#00ff88' : '#ff4444'
                            }}
                          >
                            {interaction.scoreChange > 0 ? '+' : ''}{interaction.scoreChange}
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );

  if (embedded) {
    return content;
  }

  return createPortal(
    <div className="crew-relationship-modal-overlay" onClick={onClose}>
      {content}
    </div>,
    document.body
  );
};

export default CrewRelationshipModal;